import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQueryClient } from "react-query";

import Modal from "../Shared/Modal";
import { CDN_URL } from "../../services/config";
import classNames from "./../../utils/classNames";
import SwitchInput from "../Shared/Inputs/SwitchInput";
import { TextInput } from "../Shared/Inputs/TextInput";
import { socialActionType } from "../../constants/enums";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import useAllMediaAdmin from "../../data/useAllMediaAdmin";
import InsertMediaModal from "../MediaManager/InsertMediaModal";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useSocialScheduleMutations from "../../data/useSocialScheduleMutations";

export const SinglePost = ({ post, schedule, nextPost, handleRemovePost, index }) => {
  const { agencyId, pageId } = useParams();
  const queryClient = useQueryClient();
  const { media } = useAllMediaAdmin(agencyId);
  const { updatePost } = useSocialScheduleMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Post");
  const [featuredMediaOpen, setFeaturedMediaOpen] = useState(false);
  const [instagramMediaOpen, setInstagramMediaOpen] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActive: post.isActive,
      isAds: post.isAds,
      mediaManagerId: post.mediaManagerId,
      instagramMediaId: post.instagramMediaId,
      message: post.message,
      linkUrl: post.linkUrl,
      instagramImageUrl: post.instagramImageUrl,
      useOgImage: post.useOgImage, //It used to say Use OG Image, we add a second variable for clarity
      useOwnImage: post.useOwnImage,
    },
  });

  const isActiveValue = watch("isActive");
  const isAdsValue = watch("isAds");
  const useOgImageValue = watch("useOgImage");
  const useOwnImageValue = watch("useOwnImage");
  const mediaManagerIdValue = watch("mediaManagerId");
  const instagramMediaIdValue = watch("instagramMediaId");

  useEffect(() => {
    //if the values are different, update form values
    if (post.isActive !== isActiveValue) {
      setValue("isActive", post.isActive);
    }

    if (post.isAds !== isAdsValue) {
      setValue("isAds", post.isAds);
    }
  }, [post.isActive, post.isAds, isActiveValue, isAdsValue]);

  const onSubmit = (data) => {
    // console.log(data);
    const updatedPost = {
      ...post,
      ...data,
    };

    //It used to say Use OG Image, so we need to flip it
    updatedPost.useOgImage = !data.useOwnImage;

    // console.log(updatedPost);
    updatePost.mutate(updatedPost, saveHandlers);
  };

  const handleToggleChange = (registerName, e) => {
    let updatedPost = {
      ...post,
    };

    if (registerName === "isActive") {
      updatedPost.isActive = e;
      setValue("isActive", e);
    } else if (registerName === "isAds") {
      updatedPost.isAds = e;
      setValue("isAds", e);
    }

    updatePost.mutate(updatedPost);
  };

  const handleFeaturedMediaChange = (_, __, media) => {
    queryClient.refetchQueries("allMedia");

    setTimeout(() => {
      setValue("mediaManagerId", media.id, { shouldDirty: true });
      setFeaturedMediaOpen(false);
    }, 1000);
  };

  const handleInstagramMediaChange = (_, __, media) => {
    queryClient.refetchQueries("allMedia");

    setTimeout(() => {
      setValue("instagramMediaId", media.id, { shouldDirty: true });
      setInstagramMediaOpen(false);
    }, 1000);
  };

  const getMediaPreview = (mediaManagerIdValue, classes) => {
    const m = media.data.find((m) => m.id === mediaManagerIdValue);

    if (m != null) {
      return m.type.includes("video") ? (
        <video
          className={classNames(`w-full object-contain`, classes)}
          controls
        >
          <source src={`${CDN_URL}${m.path}`} type={m.type} />
        </video>
      ) : (
        <img
          src={`${CDN_URL}${m.path}`}
          alt={m.altText}
          className={classNames(`w-full object-contain`, classes)}
        />
      );
    } else {
      return (
        <img
          src={post.ogImageUrl}
          alt="OG"
          className={classNames(`w-full object-contain`, classes)}
        />
      );
    }
  };

  return (
    <>
      <InsertMediaModal
        allowVideo={true}
        allowURL={false}
        open={featuredMediaOpen}
        setOpen={setFeaturedMediaOpen}
        onInsert={handleFeaturedMediaChange}
      />
      <InsertMediaModal
        allowVideo={true}
        allowURL={false}
        open={instagramMediaOpen}
        setOpen={setInstagramMediaOpen}
        onInsert={handleInstagramMediaChange}
      />
      <form onSubmit={handleSubmit(onSubmit)} className="p-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">Post {index + 1}</div>
          <div>
            <div
              className={classNames(
                useOwnImageValue && "hover:bg-gray-200",
                "flex items-center justify-center cursor-pointer h-80 bg-gray-100"
              )}
              onClick={() => {
                useOwnImageValue && setFeaturedMediaOpen(true);
              }}
            >
              {((useOwnImageValue && mediaManagerIdValue) || (!useOwnImageValue && post.ogImageUrl)) && !media.isLoading && media.data ? (
                getMediaPreview(useOwnImageValue ? mediaManagerIdValue : null, "max-h-80")
              ) : (
                <div className="w-full h-80 p-1 text-sm bg-gray-100 flex flex-col items-center justify-center">
                  <p className="text-center">
                    No Image/Video{" "}
                    {useOwnImageValue ? "Chosen" : "Found From Link"}
                  </p>
                  {useOwnImageValue && (
                    <p className="text-center">Click to add</p>
                  )}
                </div>
              )}
            </div>

            {/* Instagram Image Section */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Instagram Image/Video (Optional)
              </h3>
              <div
                className="flex items-center justify-center cursor-pointer h-40 bg-gray-100 hover:bg-gray-200"
                onClick={() => setInstagramMediaOpen(true)}
              >
                {instagramMediaIdValue && !media.isLoading && media.data ? (
                  getMediaPreview(instagramMediaIdValue, "max-h-40")
                ) : (
                  <div className="w-full h-40 p-1 text-sm bg-gray-100 flex flex-col items-center justify-center">
                    <p className="text-center">No Instagram Image/Video Chosen</p>
                    <p className="text-center">Click to add</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-grow justify-between pt-2">
            <div className="space-y-4 w-1/2">
              <SwitchInput
                label="Active"
                register={register}
                registerName={"isActive"}
                setValue={handleToggleChange}
                watch={watch}
              />
              <SwitchInput
                label="Use Own Image"
                register={register}
                registerName={"useOwnImage"}
                setValue={setValue}
                watch={watch}
              />
              {nextPost && nextPost.id === post.id && (
                <div className="!mt-10">
                  <span className="px-4 py-2 rounded-full font-bold text-sm bg-blue-100 text-blue-600">
                    Next Post To Run
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <TextInput
                label={"Message"}
                register={register}
                registerName={"message"}
                richtext
                rows={4}
              />
              <TextInput
                label={"Link URL"}
                register={register}
                registerName={"linkUrl"}
              />
              {schedule?.trigger?.triggerType == 2 && (
                <TextInput
                  label={"Instagram Link URL"}
                  register={register}
                  registerName={"instagramImageUrl"}
                />
              )}
              <div className="flex gap-x-2">
                <ButtonInput
                  label="Save"
                  classes="!w-1/2"
                  isSubmit={true}
                  onClick={() => { }}
                />
                <ButtonInput
                  label="Delete"
                  classes="!w-1/2 !bg-red-600 hover:!bg-red-700"
                  isSubmit={false}
                  onClick={() => handleRemovePost(post.id)}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

// Create a reverse lookup object for readability
const reverseLookup = {};
for (const key in socialActionType) {
  reverseLookup[socialActionType[key]] = key;
}

export default function AddEditSocialPostsModal({ open, setOpen, schedule }) {
  const { agencyId, pageId } = useParams();
  const { saveHandlers, deleteHandlers } =
    useDefaultCRUDHandlers("Social Posts");
  const { addEmptyPost, removePost, updateToggles } =
    useSocialScheduleMutations();
  // const [trigger, setTrigger] = useState();
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActiveAll: false,
      isAdsAll: false,
    },
  });

  const handleNewPost = () => {
    //Create a new post
    addEmptyPost.mutate(schedule.id, saveHandlers);
  };

  const handleRemovePost = (postId) => {
    const ids = {
      scheduleId: schedule.id,
      postId: postId,
    };

    removePost.mutate(ids, deleteHandlers);
  };

  const handleAllToggleChange = (registerName, e) => {
    let toggleBody = {
      socialScheduleId: schedule.id,
      isActive: null,
      isAds: null,
    };

    if (registerName === "isActiveAll") {
      toggleBody.isActive = e;

      schedule.postData?.forEach((post) => {
        post.isActive = e;
      });

      setValue("isActiveAll", e);
    } else if (registerName === "isAdsAll") {
      toggleBody.isAds = e;

      schedule.postData?.forEach((post) => {
        post.isAds = e;
      });

      setValue("isAdsAll", e);
    }

    updateToggles.mutate(toggleBody);
  };

  useEffect(() => {
    //Figure out if all are active or all are ads
    let isActiveAll = false;
    let isAdsAll = false;

    if (schedule?.postData?.length > 0) {
      isActiveAll = schedule.postData?.every((post) => post.isActive);
      isAdsAll = schedule.postData?.every((post) => post.isAds);
    }

    setValue("isActiveAll", isActiveAll);
    setValue("isAdsAll", isAdsAll);
  }, [schedule]);

  if (!schedule) {
    return null;
  }

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-[80vw]">
      <div className="p-4 space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-semibold">Posts for {schedule?.name}</h1>
          <form className="flex items-center gap-x-4">
            <SwitchInput
              label="All Active"
              register={register}
              registerName={"isActiveAll"}
              setValue={handleAllToggleChange}
              watch={watch}
            />
            {/* <SwitchInput
              label="All Ads"
              register={register}
              registerName={"isAdsAll"}
              setValue={handleAllToggleChange}
              watch={watch}
            /> */}
          </form>
        </div>

        {/* Map each post data and display */}
        <div className="grid grid-cols-1">
          {!schedule?.postData && schedule.postData?.length < 1 ? (
            <div className="col-span-1">No posts found</div>
          ) : (
            schedule?.postData?.map((post, i) => (
              <div
                key={post.id}
                //Middle borders only
                className={`p-2 
                  ${i % 2 === 0 || i === schedule.postData.length - 1
                    ? ""
                    : "border-b"
                  } 
                  ${i < schedule.postData.length - 2 ? "border-b" : ""} 
                  ${i % 2 === 0 ? "border-r" : ""} 
                  `}
              >
                <SinglePost
                  post={post}
                  schedule={schedule}
                  nextPost={schedule.nextPost}
                  handleRemovePost={handleRemovePost}
                  index={i}
                />
              </div>
            ))
          )}
        </div>

        <hr />

        <div>
          <ButtonInput
            label="Add New Post"
            isSubmit={false}
            onClick={() => handleNewPost()}
          />
        </div>
      </div>
    </Modal>
  );
}
